import type { AppConfig } from '@/types/config'

/** Top-level category slugs — must match CMS category slugs on the API */
export const MOTORSPORT_CATEGORY_SLUGS = {
  leMans: 'le-mans',
  f1: 'formula-1',
  engineers: 'motorsport-engineers',
  modelCars: 'model-cars',
  americanMuscle: 'american-muscle-cars',
  raceTracks: 'famous-race-tracks',
  raceTeams: 'famous-race-teams',
  teamPrinciples: 'famous-race-team-principles',
} as const

const categorySection = (title: string, slug: string) => ({
  type: 'titles' as const,
  layout: 'horizontal' as const,
  data: {
    title,
    list: [],
    params: {
      'filter[category]': slug,
      'filter[is_subscription]': 1,
    },
  },
  title,
  limit: 20,
})

export const motorsportConfig: AppConfig = {
  api: {
    baseUrl: import.meta.env.VITE_API_URL,
    appUrl: import.meta.env.VITE_APP_URL,
    version: 'v1',
  },
  app: {
    id: 'motorsport',
    name: 'Motorsport',
    description: 'PDF magazines and posters — Le Mans, F1, race teams, tracks, and more',
    contentId: 7,
    subscriptionPlanId: 7,
    logo: {
      default: '/assets/images/motorsport/logo.svg',
      icon: '/assets/images/motorsport/icon.svg',
    },
  },

  theme: {
    colors: {
      // Fire engine red (slightly deeper than the previous primary)
      primary: '#C41E3A',
      secondary: '#1A1A1A',
      accent: '#C41E3A',
      background: '#F5F5F5',
    },
    dark: {
      primary: '#C41E3A',
      secondary: '#E5E5E5',
      accent: '#C41E3A',
      background: '#0D0D0D',
    },
    fonts: {
      heading: 'Inter',
      body: 'Inter',
    },
    form: {
      padding: {
        container: 'py-8 px-4 sm:px-10',
        field: 'space-y-4',
      },
      rounding: {
        button: 'rounded-md',
        input: 'rounded-md',
      },
    },
  },

  layout: {
    maxWidth: '2xl',
    headerHeight: '16',
    playerHeight: '0px',
    screens: {
      home: [
        {
          type: 'titles',
          layout: 'horizontal',
          data: {
            title: 'Latest Articles',
            list: [],
            params: {
              'filter[is_subscription]': 1,
              sort: '-published_at',
            },
          },
          title: 'Latest Articles',
          limit: 20,
        },
        categorySection('Le Mans', MOTORSPORT_CATEGORY_SLUGS.leMans),
        categorySection('Formula 1', MOTORSPORT_CATEGORY_SLUGS.f1),
        categorySection('Motorsport Engineers', MOTORSPORT_CATEGORY_SLUGS.engineers),
        categorySection('Model Cars', MOTORSPORT_CATEGORY_SLUGS.modelCars),
        categorySection('American Muscle Cars', MOTORSPORT_CATEGORY_SLUGS.americanMuscle),
        categorySection('Famous Race Tracks', MOTORSPORT_CATEGORY_SLUGS.raceTracks),
        categorySection('Famous Race Teams', MOTORSPORT_CATEGORY_SLUGS.raceTeams),
        categorySection('Famous Race Team Principles', MOTORSPORT_CATEGORY_SLUGS.teamPrinciples),
        {
          type: 'categories',
          layout: 'masonry',
          data: {
            title: 'Browse All Topics',
            list: [],
            params: {
              limit: 12,
            },
          },
          title: 'Browse All Topics',
          limit: 12,
        },
      ],
      search: [
        {
          type: 'categories',
          layout: 'masonry',
          data: {
            title: 'Browse by Topic',
            list: [],
            params: {
              limit: 12,
            },
          },
          title: 'Browse by Topic',
          limit: 12,
        },
      ],
      library: [
        {
          type: 'titles',
          layout: 'horizontal',
          id: 'recently_viewed',
          data: {
            title: 'Recently Viewed',
            list: [],
            params: {
              'filter[library]': 'played',
            },
          },
          title: 'Recently Viewed',
        },
        {
          type: 'titles',
          layout: 'horizontal',
          id: 'liked',
          data: {
            title: 'Saved',
            list: [],
            params: {
              'filter[library]': 'liked',
            },
          },
          title: 'Saved',
        },
        {
          type: 'titles',
          layout: 'horizontal',
          id: 'bookmarked',
          data: {
            title: 'Reading List',
            list: [],
            params: {
              'filter[library]': 'bookmarked',
            },
          },
          title: 'Reading List',
        },
      ],
    },
  },

  features: {
    shop: false,
    ads: false,
    guestMode: true,
    favorites: true,
    darkMode: true,
    search: true,
    player: false,
    sharing: true,
    epubReader: false,
    video: false,
    pdfReader: true,
    hasPreview: false,
  },

  defaultMode: 'dark' as const,

  subscription: {
    stripePlanId: 'prod_Ub1hJuY0qiuPfH',
    price: '2.00',
    currency: 'GBP',
    features: [
      'All PDF magazines & posters',
      'New articles several times a week',
      'Rolling monthly subscription',
      'Cancel anytime',
    ],
    usps: [
      {
        title: 'Full library access',
        description:
          'Read every article across Le Mans, F1, race teams, tracks, engineers, model cars, and more.',
        icon: 'book',
      },
      {
        title: 'Fresh content weekly',
        description: 'New PDF magazines and posters added regularly once the app is live.',
        icon: 'timer',
      },
      {
        title: '£2 per month',
        description: 'Simple rolling subscription — cancel whenever you like.',
        icon: 'subscribe',
      },
      {
        title: 'Organised by topic',
        description:
          'Browse main subjects and sub-categories, from Le Mans history to famous race team principles.',
        icon: 'premium',
      },
    ],
  },

  urls: {
    terms: 'https://motorsport.zike.app/terms',
    privacy: 'https://motorsport.zike.app/privacy',
    support: 'https://motorsport.zike.app/support',
  },

  seo: {
    title: 'Motorsport — PDF magazines & posters',
    description:
      'Subscription access to motorsport PDF magazines and posters: Le Mans, F1, race teams, tracks, and more.',
    ogImage: '/assets/images/motorsport/og-image.svg',
    defaults: {
      titles: {
        title: 'Article | Motorsport',
        description: 'Read this motorsport PDF magazine or poster',
        type: 'article',
      },
      categories: {
        title: 'Topic | Motorsport',
        description: 'Browse motorsport articles in this topic',
        type: 'website',
      },
      series: {
        title: 'Collection | Motorsport',
        description: 'Articles in this motorsport collection',
        type: 'website',
      },
    },
  },
}
